import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { submitLead, formatApiError, BUSINESS_TYPE_OPTIONS, BUSINESS_SIZE_OPTIONS, SERVICE_PAGES } from "../lib/api";

export default function LeadForm({ service = null, heading, subheading, dark = false }) {
  const [form, setForm] = useState({
    contact_name: "", business_name: "", email: "", phone: "", comments: "",
    business_type: "", business_size: "", consent_privacy: false, consent_marketing: false,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const honeypot = useRef("");
  const startedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const validate = () => {
    const errs = {};
    if (!form.contact_name.trim()) errs.contact_name = "Please add your name.";
    if (!form.business_name.trim()) errs.business_name = "Please add your business name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Please add a valid business email.";
    if (!form.business_type) errs.business_type = "Select your business type.";
    if (!form.business_size) errs.business_size = "Select your team size.";
    if (!form.consent_privacy) errs.consent_privacy = "Please accept the privacy policy.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await submitLead({
        ...form,
        interests: [service || "unsure"],
        source_page: window.location.pathname + window.location.search,
        source_service: service,
        honeypot: honeypot.current ? honeypot.current.value : "",
        elapsed_ms: Date.now() - startedAt.current,
      });
      setRef(res.lead_id);
      toast.success("Request received — thank you!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (ref) {
    return (
      <div className={`rounded-3xl p-8 md:p-10 text-center border ${dark ? "bg-white/5 border-white/15" : "bg-white border-line shadow-card"}`} data-testid="lead-form-success">
        <CheckCircle2 className="w-12 h-12 text-emeraldx mx-auto" />
        <h3 className={`mt-4 font-display font-extrabold tracking-tight text-2xl ${dark ? "text-white" : "text-navy"}`}>
          Your request is in.
        </h3>
        <p className={`mt-3 text-[15px] leading-relaxed ${dark ? "text-ice/70" : "text-slate"}`}>
          Thank you for sharing a little about your business. We'll review your interests in{" "}
          <strong>{service ? SERVICE_PAGES[service]?.short : "your opportunities"}</strong> and follow up with
          helpful next steps.
        </p>
        <p className={`mt-4 text-xs ${dark ? "text-ice/45" : "text-slate-light"}`}>Reference: {ref.slice(0, 8).toUpperCase()}</p>
      </div>
    );
  }

  const inputCls = (k) =>
    `w-full rounded-xl border px-4 py-3 text-[15px] bg-white text-navy placeholder:text-slate-light focus:outline-none focus:ring-2 focus:ring-teal/60 transition-shadow ${errors[k] ? "border-red-400" : "border-line"}`;

  const svc = service ? SERVICE_PAGES[service] : null;

  return (
    <form onSubmit={onSubmit} noValidate className={`rounded-3xl p-6 md:p-8 border ${dark ? "bg-white/5 border-white/15" : "bg-white border-line shadow-card"}`} data-testid="lead-form">
      <h3 className={`font-display font-extrabold tracking-tight text-xl md:text-2xl ${dark ? "text-white" : "text-navy"}`}>
        {heading || (svc ? `Request a ${svc.short} Review` : "Request Your Savings Review")}
      </h3>
      {subheading && <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-ice/65" : "text-slate"}`}>{subheading}</p>}
      {svc && (
        <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-soft px-3.5 py-1.5 text-xs font-bold text-teal-dark" data-testid="lead-form-preselected-service">
          <ShieldCheck className="w-3.5 h-3.5" /> Pre-selected: {svc.short}
        </span>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-contact" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Your name *</label>
          <input id="lf-contact" data-testid="lead-form-contact-name" className={inputCls("contact_name")} value={form.contact_name} onChange={set("contact_name")} placeholder="Jane Smith" autoComplete="name" />
          {errors.contact_name && <p className="mt-1 text-xs text-red-500">{errors.contact_name}</p>}
        </div>
        <div>
          <label htmlFor="lf-business" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Business name *</label>
          <input id="lf-business" data-testid="lead-form-business-name" className={inputCls("business_name")} value={form.business_name} onChange={set("business_name")} placeholder="Smith & Co." autoComplete="organization" />
          {errors.business_name && <p className="mt-1 text-xs text-red-500">{errors.business_name}</p>}
        </div>
        <div>
          <label htmlFor="lf-email" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Business email *</label>
          <input id="lf-email" data-testid="lead-form-email" type="email" className={inputCls("email")} value={form.email} onChange={set("email")} placeholder="jane@yourbusiness.com" autoComplete="email" />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="lf-phone" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Phone (optional)</label>
          <input id="lf-phone" data-testid="lead-form-phone" type="tel" className={inputCls("phone")} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="lf-type" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Business type *</label>
          <select id="lf-type" data-testid="lead-form-business-type" className={inputCls("business_type")} value={form.business_type} onChange={set("business_type")}>
            <option value="">Select…</option>
            {BUSINESS_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {errors.business_type && <p className="mt-1 text-xs text-red-500">{errors.business_type}</p>}
        </div>
        <div>
          <label htmlFor="lf-size" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Team size *</label>
          <select id="lf-size" data-testid="lead-form-business-size" className={inputCls("business_size")} value={form.business_size} onChange={set("business_size")}>
            <option value="">Select…</option>
            {BUSINESS_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {errors.business_size && <p className="mt-1 text-xs text-red-500">{errors.business_size}</p>}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="lf-comments" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Anything you'd like us to know? (optional)</label>
          <textarea id="lf-comments" data-testid="lead-form-comments" rows="3" className={inputCls("comments")} value={form.comments} onChange={set("comments")} placeholder="Tell us a little about what you'd like to explore…" />
        </div>
      </div>

      {/* honeypot — hidden from humans */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="lf-website">Website</label>
        <input id="lf-website" name="website_url" tabIndex={-1} autoComplete="off" ref={honeypot} />
      </div>

      <div className="mt-5 space-y-3">
        <label className={`flex items-start gap-3 text-[13px] leading-relaxed ${dark ? "text-ice/70" : "text-slate"}`}>
          <input type="checkbox" data-testid="lead-form-consent-privacy" className="mt-0.5 w-4 h-4 accent-[#15B8A6]" checked={form.consent_privacy} onChange={(e) => setForm((f) => ({ ...f, consent_privacy: e.target.checked }))} />
          <span>
            I agree to be contacted about my inquiry and accept the{" "}
            <Link to="/privacy" className="underline font-semibold text-teal-dark">Privacy Policy</Link>. *
          </span>
        </label>
        {errors.consent_privacy && <p className="text-xs text-red-500">{errors.consent_privacy}</p>}
        <label className={`flex items-start gap-3 text-[13px] leading-relaxed ${dark ? "text-ice/70" : "text-slate"}`}>
          <input type="checkbox" data-testid="lead-form-consent-marketing" className="mt-0.5 w-4 h-4 accent-[#15B8A6]" checked={form.consent_marketing} onChange={(e) => setForm((f) => ({ ...f, consent_marketing: e.target.checked }))} />
          <span>Occasionally send me new savings opportunities worth knowing about. (optional)</span>
        </label>
      </div>

      <button
        type="submit"
        data-testid="lead-form-submit"
        disabled={busy}
        className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-bold text-[15px] px-6 py-4 rounded-xl transition-all duration-300 hover:bg-teal hover:text-navy disabled:opacity-60 disabled:pointer-events-none"
      >
        {busy ? "Sending…" : svc ? "Request My Review" : "Start My Savings Review"}
        <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
      <p className={`mt-4 text-[11px] leading-relaxed ${dark ? "text-ice/45" : "text-slate-light"}`}>
        No pressure and no obligation. Evaluations are performed by independent specialists; Bottom Line
        Improvements is not an insurance provider, accounting practice, or tax advisory firm. We may receive
        referral compensation when customers choose services through participating provider relationships.
      </p>
    </form>
  );
}
